<script lang="ts">
  import { DataTable, currencyPHP } from '@epitome-analytics/dashboard-kit';
  import { store, type TagSignal } from '../lib/stores.svelte.js';
  import { fetchCityRows, type CityRow } from '../lib/queries.js';
  import { formatTier, fmtShare } from '../lib/format.js';
  import TagFilterBar from './TagFilterBar.svelte';

  let rows    = $state<CityRow[]>([]);
  let loading = $state(true);
  let error   = $state<string | null>(null);

  $effect(() => {
    void store.period; void store.principal; void store.productType; void store.brand; void store.sku_id;
    if (!store.period) return;
    loading = true; error = null;
    fetchCityRows(store.period)
      .then((r) => { rows = r; loading = false; })
      .catch((e) => { error = (e as Error).message; loading = false; });
  });

  /* Filter by search + tag */
  const filtered = $derived(() => {
    const q = store.citySearch.trim().toLowerCase();
    let out = rows;
    if (q) out = out.filter((r) =>
      r.city.toLowerCase().includes(q) ||
      r.province.toLowerCase().includes(q));
    if (store.tagFilter && store.tagFilter !== 'All') {
      const sig = store.tagFilter;
      out = out.filter((r) => {
        const t = store.tagged.get(store.tagKey(r.city_id, r.sku_id ?? -1));
        return t?.signal === sig;
      });
    }
    return out;
  });

  /* Tag a row with a heuristic signal based on its metrics. */
  function recommendSignal(r: CityRow): TagSignal {
    if (r.share < 0.10 && r.cat_gmv > 1_000_000) return 'Distribution Push';
    if (r.share > 0.35)                          return 'Key Account';
    return 'Trade Promotion';
  }
  function toggleTag(r: CityRow) {
    const skuId = r.sku_id ?? -1;
    if (store.isTagged(r.city_id, skuId)) {
      store.untag(r.city_id, skuId);
    } else {
      store.setTag({
        city_id: r.city_id, city: r.city, province: r.province,
        brand: r.brand, sku_id: skuId, sku: r.sku,
        signal: recommendSignal(r),
        gpiv: r.gpiv, sop: r.sop, cat_gmv: r.cat_gmv, my_gmv: r.my_gmv,
        share: r.share, mp: r.mp, captured_at: store.period,
      });
    }
  }

  const columns = [
    { key: 'city' as const,     label: 'City' },
    { key: 'province' as const, label: 'Province' },
    {
      key: 'gpiv_tier' as const, label: 'Signal', align: 'center' as const,
      format: (_v: unknown, r: CityRow) => `<span style="color:${formatTier(r.gpiv_tier).color}">●</span> ${formatTier(r.gpiv_tier).label}`,
    },
    { key: 'gpiv' as const,    label: 'GPIv',        align: 'right' as const, format: (v: unknown) => Number(v).toFixed(2) },
    { key: 'sop' as const,     label: 'Size of Prize', align: 'right' as const, format: (v: unknown) => currencyPHP.formatPHPShort(Number(v)) },
    { key: 'cat_gmv' as const, label: 'Cat GMV',     align: 'right' as const, format: (v: unknown) => currencyPHP.formatPHPShort(Number(v)) },
    { key: 'my_gmv' as const,  label: 'My GMV',      align: 'right' as const, format: (v: unknown) => currencyPHP.formatPHPShort(Number(v)) },
    { key: 'share' as const,   label: 'Share',       align: 'right' as const, format: (v: unknown) => fmtShare(Number(v)) },
    { key: 'mp' as const,      label: 'Mkt Pot',     align: 'center' as const, format: (v: unknown) => String(v) },
    {
      key: 'city_id' as const, label: '★', align: 'center' as const, width: '40px',
      format: (_v: unknown, r: CityRow) => store.isTagged(r.city_id, r.sku_id ?? -1) ? '★' : '☆',
    },
  ];

  function onRowClick(r: CityRow) {
    if (r.city_id !== store.selectedCity) store.selectCity(r.city_id);
  }

  function onTableClick(e: MouseEvent) {
    const td = (e.target as HTMLElement).closest('td');
    if (!td) return;
    const tr = td.closest('tr');
    if (!tr) return;
    const cells = Array.from(tr.parentElement?.querySelectorAll('tr') ?? []);
    const ri = cells.indexOf(tr as HTMLTableRowElement) - 1;
    if (ri < 0 || ri >= filtered().length) return;
    const isStarCell = (tr as HTMLElement).lastElementChild === td;
    if (isStarCell) {
      e.stopPropagation();
      toggleTag(filtered()[ri]);
    }
  }
</script>

<section class="rankings">
  <div class="toolbar">
    <input
      type="search"
      placeholder="Search city or province…"
      value={store.citySearch}
      oninput={(e) => (store.citySearch = (e.currentTarget as HTMLInputElement).value)}
    />
    <TagFilterBar />
    <span class="count">{filtered().length} of {rows.length}</span>
  </div>

  {#if error}
    <div class="banner">{error}</div>
  {:else if loading}
    <div class="banner muted">Loading rankings…</div>
  {:else if filtered().length === 0}
    <div class="banner muted">No cities match the current filters / period.</div>
  {:else}
    <div onclick={onTableClick} role="presentation">
      <DataTable
        rows={filtered()}
        columns={columns}
        rowKey={(r) => `${r.city_id}|${r.sku_id ?? -1}`}
        onRowClick={onRowClick}
      />
    </div>
  {/if}
</section>

<style>
  .rankings {
    display: flex;
    flex-direction: column;
    gap: var(--space-md);
  }
  .toolbar {
    display: flex;
    align-items: center;
    gap: var(--space-md);
    flex-wrap: wrap;
  }
  input[type="search"] {
    font-family: var(--font-ui);
    font-size: var(--text-sm);
    padding: 8px 12px;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-sm);
    background: var(--color-white);
    min-width: 280px;
    color: var(--color-text);
  }
  input[type="search"]:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
    border-color: var(--color-blue);
  }
  .count {
    margin-left: auto;
    font-family: var(--font-ui);
    font-size: var(--text-xs);
    color: var(--color-text-muted);
  }
  .banner {
    padding: var(--space-md) var(--space-lg);
    border-radius: var(--radius-md);
    background: var(--color-negative-bg);
    color: var(--color-negative);
    font-family: var(--font-ui);
    font-size: var(--text-sm);
  }
  .banner.muted { background: var(--color-info-bg); color: var(--color-info); }
</style>
